import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        try {
          // Fetch user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserData(userData);
            setIsAdmin(userData.userType === 'admin');
          } else {
            // Create mock user data for development
            const mockUserData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              fullName: firebaseUser.displayName || 'Test User',
              userType: 'student', // Default to student
              location: 'Yaoundé',
              createdAt: new Date()
            };
            setUserData(mockUserData);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Mock data for development
          const mockUserData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            fullName: firebaseUser.displayName || 'Test User',
            userType: 'student',
            location: 'Yaoundé',
            createdAt: new Date()
          };
          setUserData(mockUserData);
          setIsAdmin(false);
        }
      } else {
        setUserData(null);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    userData,
    loading,
    isAdmin
  };

  // Update user data in Firestore and local context
  const updateUserData = async (updates = {}) => {
    if (!user) throw new Error('No authenticated user');
    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, updates, { merge: true });
      const updated = await getDoc(userRef);
      if (updated.exists()) setUserData(updated.data());
      return updated.exists() ? updated.data() : null;
    } catch (err) {
      console.error('Failed to update user data:', err);
      throw err;
    }
  };

  // include updateUserData in provided context value
  value.updateUserData = updateUserData;

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};